import {
  all,
  put,
  takeEvery,
  call,
  select,
  take,
  delay,
  race
} from 'redux-saga/effects'

import { fromJS, Set as ImmutableSet } from 'immutable'
import moment from 'moment-timezone'

import * as Api from 'api/contacts_management'
import * as BffApi from 'api/bff'
import * as AppActions from 'actions/app'
import * as Actions from 'actions/contact_management'
import * as NewsActions from 'actions/news'
import * as SavedSearchesActions from 'actions/saved_searches'
import * as FilterActions from 'actions/filter'
import * as NavigationActions from 'actions/navigation'
import * as Selectors from 'selectors'

import { SearchFields } from 'static/constants'

// Contact Detail Page Sagas
// 1. Fetch Contact
// 2. Create Contact
// 3. Update Contact
// 4. Fetch Email Events
// 5. Fetch Earned Media
// 6. Reset Contact
// 7. Report Error
// 8. Upload Picture
// 9. Add to list

// 1. Fetch Contact
export function* fetchContact() {
  const contactId = yield select(Selectors.getCmUiDetailPageContactId)

  if (!contactId) {
    return
  }

  try {
    const { contact } = yield call(Api.fetchContactById, contactId)
    yield put(Actions.fetchContactSuccess({ contact }))
    yield put(Actions.fetchEmailEventsStart({ contactId, recipientEmail: contact.mail }))
    yield put(Actions.fetchEarnedMediaStart({ contactId, recipientEmail: contact.mail }))
  } catch (error) {
    yield put(Actions.fetchContactError())
  }

  // Wait until detail page is closed
  yield take([Actions.closeDetailPages, Actions.closeContactDetailsPage])
  yield put(Actions.clearDetailPage())
}

// 2. Create Contact
export function* createContact({ payload }) {
  const i18n = yield select(Selectors.getI18n)
  const newContactData = yield select(Selectors.getCmUiNewContactData)
  const { contact, editAfterCreate, forceSuggestionCheck } = payload

  let postBody

  if (contact) {
    postBody = {
      first_name: contact.get('firstName'),
      last_name: contact.get('lastName'),
      mail: contact.get('mail'),
      edit_after_create: editAfterCreate,
      force_suggestion_check: forceSuggestionCheck
    }
  } else {
    postBody = {
      first_name: newContactData.get('firstName'),
      last_name: newContactData.get('lastName'),
      mail: newContactData.get('email'),
      edit_after_create: editAfterCreate,
      force_suggestion_check: forceSuggestionCheck
    }
  }

  try {
    const result = yield call(Api.createContact, postBody)

    if (result.suggestion) {
      yield put(Actions.setSuggestedContactData(result))
      yield put(Actions.closeContactCreateDialog())
      yield put(Actions.openContactSuggestDialog())
      yield put(Actions.createContactError())

      return
    }

    const successData = { ...result, editAfterCreate }
    yield put(Actions.createContactSuccess(successData))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('contact_created'),
      variant: 'success'
    }))
  } catch (error) {
    let msg = i18n.get('unknown_error')

    if (error.response) {
      if (error.response.statusCode === 400) {
        msg = i18n.get('contact_invalid_data_error')
      }

      if (error.response.statusCode === 409) {
        msg = i18n.get('contact_duplicate_error')
      }
    }

    yield put(Actions.createContactError())
    yield put(AppActions.showAppSnackbarMessage({
      text: msg,
      variant: 'error'
    }))
  }
}

// 3. Update Contact
export function* updateContact({ payload }) {
  const i18n = yield select(Selectors.getI18n)
  const { id, attributes } = payload

  const body = { attributes }

  try {
    yield put(Actions.fakeUpdateContact(payload))

    const { contact } = yield call(Api.updateContact, id, body)

    yield put(Actions.updateContactSuccess(contact))

    if (Object.prototype.hasOwnProperty.call(attributes, 'distribution_list_ids')) {
      yield put(Actions.fetchDistributionListsStart())
    }

    yield put(AppActions.showAppSnackbarMessage({
      text: `${i18n.get('contact_update_success')}`,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.revertFakeUpdateContact({ id }))
    yield put(Actions.updateContactError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('contact_update_failed'),
      variant: 'error'
    }))
  }
}

// 4. Fetch Earned Media
export function* fetchEarnedMedia({ payload }) {
  const { contactId, recipientEmail } = payload

  try {
    const body = {
      recipient_email: recipientEmail
    }
    const result = yield call(Api.fetchEarnedMediaByContactId, contactId, body)
    yield put(Actions.fetchEarnedMediaSuccess({
      earnedMedia: result
    }))
  } catch (error) {
    yield put(Actions.fetchEarnedMediaError())
  }
}

// 5. Fetch Email Events
export function* fetchEmailEvents({ payload }) {
  const { contactId, recipientEmail } = payload

  try {
    const body = {
      recipient_email: recipientEmail
    }
    const result = yield call(Api.fetchEmailEventsByContactId, contactId, body)
    yield put(Actions.fetchEmailEventsSuccess({ emailEvents: result.contact }))
  } catch (error) {
    yield put(Actions.fetchEmailEventsError())
  }
}

// 6. Restore Contact
export function* resetContact({ payload }) {
  const i18n = yield select(Selectors.getI18n)
  const { id, fields } = payload

  const body = { fields }

  try {
    const { contact } = yield call(Api.resetContact, id, body)

    yield put(Actions.resetContactSuccess(contact))
    yield put(AppActions.showAppSnackbarMessage({
      text: `${i18n.get('contact_reset_success')}`,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.resetContactError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('contact_reset_failed'),
      variant: 'error'
    }))
  }
}

// 7. Report Error
export function* sendErrorReport() {
  const contact = yield select(Selectors.getCmDaDetailPageContact)
  const contactId = contact.get('id')
  const identityId = contact.get('identityId')
  const errorReport = yield select(Selectors.getCmUiContactDetailErrorReport)
  const i18n = yield select(Selectors.getI18n)

  const body = {
    contact_id: contactId,
    identity_id: identityId,
    field: errorReport.get('field')
  }

  try {
    const result = yield call(Api.sendErrorReport, contactId, body)

    yield put(Actions.sendErrorReportSuccess(result.contact))
    yield put(Actions.closeContactReportErrorDialog())
    yield put(AppActions.showAppSnackbarMessage({
      text: `${i18n.get('error_report_sent')}`,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.sendErrorReportError(error))
    yield put(Actions.closeContactReportErrorDialog())
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('error_report_failed'),
      variant: 'error'
    }))
  }
}
// 8. Upload Picture
export function* uploadPicture({ payload: { file } }) {
  const i18n = yield select(Selectors.getI18n)
  const contactId = yield select(Selectors.getCmUiDetailPageContactId)

  try {
    const body = [{
      key: 'file',
      value: file
    }]

    const result = yield call(BffApi.uploadFile, body)

    const patchBody = {
      attributes: {
        picture: result.url
      }
    }
    const { contact } = yield call(Api.updateContact, contactId, patchBody)

    yield put(Actions.updateContactSuccess(contact))
    yield put(Actions.uploadPictureSuccess())
    yield put(AppActions.showAppSnackbarMessage({
      text: `${i18n.get('picture_upload_success')}`,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.uploadPictureError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('picture_upload_failed'),
      variant: 'error'
    }))
  }
}

// 9. Add to list
export function* addContactToList({ payload: { distributionListIds, newListNames } }) {
  const i18n = yield select(Selectors.getI18n)
  const contact = yield select(Selectors.getCmDaDetailPageContact)
  const contactId = contact.get('id')
  const alreadySelectedListIds = contact.get('distributionLists')
    ? contact.get('distributionLists').map(list => list.get('id')).toArray()
    : []

  let distributionListIdsToAdd = [...ImmutableSet([...alreadySelectedListIds, ...distributionListIds])]

  try {
    if (newListNames.length > 0) {
      const results = yield all(
        newListNames.map(name => call(Api.createDistributionList, { name }))
      )

      // extract new IDs
      const newIds = results
        .filter(({ id }) => id)
        .map(({ id }) => id)

      // merge with existing + deduplicate
      distributionListIdsToAdd = Array.from(
        ImmutableSet([...distributionListIdsToAdd, ...newIds])
      )
    }

    const patchBody = {
      id: contactId,
      attributes: {
        distribution_list_ids: distributionListIdsToAdd
      }
    }

    yield updateContact({ payload: patchBody })
    yield put(Actions.addContactToListSuccess())
  } catch (error) {
    yield put(Actions.addContactToListError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('contact_update_failed'),
      variant: 'error'
    }))
  }
}

// 10. Import suggested contact
export function* importSuggestedContact({ payload: { id, editAfterCreate } }) {
  const i18n = yield select(Selectors.getI18n)

  const body = {
    ids: [id]
  }

  try {
    const result = yield call(Api.importSuggestedContact, body)
    const contact = { contact: result.contacts[0] }
    const successData = { ...contact, editAfterCreate }
    yield put(Actions.importSuggestedContactSuccess(successData))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('contact_created'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.importSuggestedContactError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('contact_invalid_data_error'),
      variant: 'error'
    }))
  }
}
// end Contact Detail Page Sagas

export function* switchToSearchPool({ payload: { field, identityId, contents } }) {
  try {
    yield put(NewsActions.blockNewsRequest())

    const isPlotlights = yield select(Selectors.isPlotlights)

    yield put(NavigationActions.navigate(isPlotlights ? '/app/media_monitoring' : '/app/search_pool'))

    const { timeout } = yield race({
      selected: take(NewsActions.viewConfigPresetSelected),
      timeout: delay(2000)
    })

    if (timeout) {
      return
    }

    yield put(NewsActions.resetNews())
    yield put(FilterActions.resetFilters())

    yield put(FilterActions.setSimilarityFilter({
      field,
      value: contents
    }))

    const { dateFrom, dateTo } = contents.reduce((acc, item) => {
      const from = item.get('from')
      const to = item.get('to')

      const fromMoment = moment(from)
      const toMoment = moment(to)

      const nextFrom = !acc.dateFrom || fromMoment.isBefore(acc.dateFrom) ? fromMoment : acc.dateFrom
      const nextTo = !acc.dateTo || toMoment.isAfter(acc.dateTo) ? toMoment : acc.dateTo

      return { dateFrom: nextFrom, dateTo: nextTo }
    }, { dateFrom: null, dateTo: null })

    yield put(FilterActions.changeDate({
      dateFrom: dateFrom.toDate(),
      dateTo: dateTo.toDate()
    }))

    if (identityId !== undefined && identityId !== null) {
      const influencerFilter = fromJS({
        id: identityId,
        name: `${identityId}`,
        identityIds: [identityId]
      })

      yield put(FilterActions.addFilters([{
        field: SearchFields.INFLUENCERS,
        filter: influencerFilter
      }]))
    }

    yield put(SavedSearchesActions.setInitialSearchLoaded(true))

    yield put(NewsActions.unblockNewsRequest())
    yield put(NewsActions.newsRequestStart())
  } catch (error) {
    yield put(AppActions.genericErrorMessage())
    yield put(AppActions.exception(error))
  } finally {
    yield put(NewsActions.unblockNewsRequest())
  }
}

// taker functions

export function* watchFetchContactStart() {
  yield takeEvery(Actions.fetchContactStart, fetchContact)
}

export function* watchCreateContactStart() {
  yield takeEvery(Actions.createContactStart, createContact)
}

export function* watchUpdateContactStart() {
  yield takeEvery(Actions.updateContactStart, updateContact)
}

export function* watchResetContactStart() {
  yield takeEvery(Actions.resetContactStart, resetContact)
}

export function* watchFetchEmailEventsStart() {
  yield takeEvery(Actions.fetchEmailEventsStart, fetchEmailEvents)
}

export function* watchFetchEarnedMediaStart() {
  yield takeEvery(Actions.fetchEarnedMediaStart, fetchEarnedMedia)
}

export function* watchSendErrorReportStart() {
  yield takeEvery(Actions.sendErrorReportStart, sendErrorReport)
}

export function* watchUploadPictureStart() {
  yield takeEvery(Actions.uploadPictureStart, uploadPicture)
}

export function* watchAddContactToListStart() {
  yield takeEvery(Actions.addContactToListStart, addContactToList)
}

export function* watchImportSuggestedContactStart() {
  yield takeEvery(Actions.importSuggestedContactStart, importSuggestedContact)
}

export function* watchSwitchToSearchPool() {
  yield takeEvery(Actions.switchToSearchPool, switchToSearchPool)
}

export default function* mainSaga() {
  yield all([
    watchFetchContactStart(),
    watchCreateContactStart(),
    watchUpdateContactStart(),
    watchResetContactStart(),
    watchFetchEmailEventsStart(),
    watchFetchEarnedMediaStart(),
    watchSendErrorReportStart(),
    watchUploadPictureStart(),
    watchAddContactToListStart(),
    watchImportSuggestedContactStart(),
    watchSwitchToSearchPool()
  ])
}
